/**
 * Smart Shikka AI Core — PR97
 * Approval Runtime Gate — Message Send Approval
 * Registry — Deep Frozen — No Broad Runtime Unlock
 */
function deepFreeze(obj) {
    if (obj && typeof obj === "object") {
        Object.freeze(obj);
        for (const key of Object.keys(obj)) {
            const val = obj[key];
            if (val && typeof val === "object" && !Object.isFrozen(val)) {
                deepFreeze(val);
            }
        }
    }
    return obj;
}
const BLOCKED_ACTIONS = [
    "agent_execution", "child_process", "old_assistant_alias",
    "permission_mutation", "role_mutation", "runtime_unlock",
    "secret_literal", "shell_command", "tool_execution",
    "unapproved_db_write", "unapproved_file_write",
    "unapproved_network_call", "unknown_provider_call",
    "unknown_tool_execution", "unredacted_secret", "unsafe_env_dump",
    "workflow_execution", "browser_action", "desktop_action",
    "file_read", "file_write", "gate_unlock",
    "broad_runtime_unlock", "unscoped_approval",
    "approval_without_audit", "expired_approval_use",
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    providerCallAllowedInPr97: false,
    secretLiteralAllowedInPr97: false,
    secretReadAllowedInPr97: false,
    runtimeUnlockAllowedInPr97: false,
    gateUnlockAllowedInPr97: false,
    agentExecutionAllowedInPr97: false,
    toolExecutionAllowedInPr97: false,
    workflowExecutionAllowedInPr97: false,
    documentRenderAllowedInPr97: false,
    iconInjectionAllowedInPr97: false,
    browserAutomationAllowedInPr97: false,
    desktopAutomationAllowedInPr97: false,
    fileMutationAllowedInPr97: false,
    fileReadAllowedInPr97: false,
    dbWriteAllowedInPr97: false,
    shellCommandAllowedInPr97: false,
    childProcessAllowedInPr97: false,
    roleMutationAllowedInPr97: false,
    permissionMutationAllowedInPr97: false,
    broadRuntimeUnlockAllowedInPr97: false,
    unscopedApprovalAllowedInPr97: false,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
};
const registry = [
    {
        id: "approval_runtime_gate_summary",
        displayName: "Approval Runtime Gate Summary",
        description: "Summary of controlled approval runtime gate for message send. Bounded approval decision only — no broad runtime unlock.",
        sourceModules: ["smart-shikka-assistant-approval-runtime-gate", "smart-shikka-assistant-controlled-chat-send", "smart-shikka-assistant-readonly-approval-state-preview"],
        targetPreviewModules: ["smart-shikka-assistant-approval-runtime-gate"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: ["readonly_conversation_state_snapshot"],
        relatedApprovalStatePreviewIds: ["readonly_approval_summary_state", "readonly_dual_approval_required_state"],
        relatedActionRequestPreviewIds: ["readonly_action_request_summary"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Bounded message send approval only", "No broad runtime unlock", "Dual approval required"],
        futurePrScope: ["Tool execution approval gate", "Agent execution approval gate"],
        notes: ["Approval scoped to message send only", "No generic runtime unlock", "Audit evidence on every decision"],
    },
    {
        id: "message_send_approval_request",
        displayName: "Message Send Approval Request",
        description: "Request contract for message send approval. Must include sender, recipient context, redacted content preview, and approval scope.",
        sourceModules: ["smart-shikka-assistant-approval-runtime-gate", "smart-shikka-assistant-controlled-chat-send"],
        targetPreviewModules: ["smart-shikka-assistant-approval-runtime-gate"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_owner_approval_required_state"],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Request must specify scope", "No open-ended approval", "Content must be redacted in request"],
        futurePrScope: ["Multi-message batch approval"],
        notes: ["Approval request is scoped and bounded", "No open-ended or blanket approval"],
    },
    {
        id: "owner_approval_decision_contract",
        displayName: "Owner Approval Decision Contract",
        description: "Contract for owner approval decision. Owner can approve or deny. Decision is recorded with timestamp and reason.",
        sourceModules: ["smart-shikka-assistant-approval-runtime-gate"],
        targetPreviewModules: ["smart-shikka-assistant-approval-runtime-gate"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_owner_approval_required_state"],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Owner decision required", "Decision must have reason", "Timestamp mandatory"],
        futurePrScope: ["Delegated owner approval", "Conditional approval rules"],
        notes: ["Decision: approve or deny", "Recorded with timestamp", "Denial reason mandatory on deny"],
    },
    {
        id: "user_approval_decision_contract",
        displayName: "User Approval Decision Contract",
        description: "Contract for user approval decision. User confirms intent to send. Decision is recorded with timestamp.",
        sourceModules: ["smart-shikka-assistant-approval-runtime-gate"],
        targetPreviewModules: ["smart-shikka-assistant-approval-runtime-gate"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_user_approval_required_state"],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["User confirmation required", "User must acknowledge send", "Timestamp mandatory"],
        futurePrScope: ["Implicit user approval for trusted contexts"],
        notes: ["User confirms send intent", "Recorded with timestamp", "Cannot be bypassed"],
    },
    {
        id: "dual_approval_decision_contract",
        displayName: "Dual Approval Decision Contract",
        description: "Contract requiring both owner and user approval before message send proceeds. Both must approve within expiry window.",
        sourceModules: ["smart-shikka-assistant-approval-runtime-gate"],
        targetPreviewModules: ["smart-shikka-assistant-approval-runtime-gate"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_dual_approval_required_state"],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Both owner and user must approve", "Either denial blocks send", "Expiry invalidates approval"],
        futurePrScope: ["Multi-party approval (3+ approvers)"],
        notes: ["Dual approval mandatory", "Order: owner first, then user", "Expiry window enforced"],
    },
    {
        id: "approval_denial_contract",
        displayName: "Approval Denial Contract",
        description: "Contract for denial outcomes. Denial blocks send permanently for that request. New request needed after denial.",
        sourceModules: ["smart-shikka-assistant-approval-runtime-gate"],
        targetPreviewModules: ["smart-shikka-assistant-approval-runtime-gate"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_approval_denied_state"],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Denial is permanent for request", "No retry on same request", "New request required"],
        futurePrScope: ["Denial appeal process"],
        notes: ["Denial blocks permanently", "Audit log records denial reason", "No override possible"],
    },
    {
        id: "approval_audit_log_contract",
        displayName: "Approval Audit Log Contract",
        description: "Every approval/denial decision generates sanitized audit evidence. No raw content in logs. All PII redacted.",
        sourceModules: ["smart-shikka-assistant-approval-runtime-gate", "smart-shikka-assistant-no-runtime-safety-audit-pack"],
        targetPreviewModules: ["smart-shikka-assistant-approval-runtime-gate"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Audit log mandatory", "No decision without log", "All content redacted in log"],
        futurePrScope: ["Audit dashboard", "Compliance export"],
        notes: ["Log: decision, timestamp, approver_ids, redacted_scope, denial_reason", "All PII → [REDACTED_PII]"],
    },
    {
        id: "approval_expiry_policy",
        displayName: "Approval Expiry Policy",
        description: "Approvals expire after bounded time window. Expired approval cannot be used. New approval required.",
        sourceModules: ["smart-shikka-assistant-approval-runtime-gate"],
        targetPreviewModules: ["smart-shikka-assistant-approval-runtime-gate"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Approval has expiry window", "Expired approval blocked", "No extending expiry"],
        futurePrScope: ["Configurable expiry per action type"],
        notes: ["Default expiry: bounded window", "expired_approval_use blocked", "Re-approval required after expiry"],
    },
    {
        id: "runtime_unlock_still_blocked",
        displayName: "Runtime Unlock Still Blocked",
        description: "This PR does NOT grant broad runtime unlock. Approval is scoped to message send only. All other runtime actions remain blocked.",
        sourceModules: ["smart-shikka-assistant-approval-runtime-gate"],
        targetPreviewModules: ["smart-shikka-assistant-approval-runtime-gate"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["No broad runtime unlock", "Only message send approval", "All other runtime blocked"],
        futurePrScope: ["Scoped runtime unlock per capability"],
        notes: ["runtime_unlock blocked", "broad_runtime_unlock blocked", "gate_unlock blocked", "Only message_send scoped"],
    },
    {
        id: "future_tool_approval_ready",
        displayName: "Future Tool Approval Ready",
        description: "Placeholder for future tool execution approval gate. Not active in PR97. Tool approval requires separate PR.",
        sourceModules: ["smart-shikka-assistant-approval-runtime-gate"],
        targetPreviewModules: ["smart-shikka-assistant-approval-runtime-gate"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Tool approval not active in PR97", "Requires separate PR", "No tool execution in PR97"],
        futurePrScope: ["Tool execution approval gate", "Per-tool approval scoping"],
        notes: ["Future placeholder", "tool_execution remains blocked", "Separate approval PR needed"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantApprovalRuntimeGateEntries() {
    return registry;
}
export function getSmartShikkaAssistantApprovalRuntimeGateEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantApprovalRuntimeGateRegistry.js.map